import styled, { css } from 'styled-components';

import Link from '~/src/common/components/Link';
import { COLORS, SPECIALS, ZINDEX } from '~/src/common/theme';
import { getMediaQuery } from '~/src/common/theme/deprecated';
import { ImageParams } from '~/src/common/typings/image';

import categoryGridStyles from '../CategoryGrid/CategoryGrid.module.scss';
import categoryItemStyles from '../CategoryItem/CategoryItem.module.scss';
import subCategoriesStyles from '../SubCategories/SubCategories.module.scss';

import { getLayout } from './config';
import styles from './MegaMenu.module.scss';

// Zones du layout du menu
const AREA_BODY = 'body';
const AREA_ASIDE = 'aside';
const AREA_FOOTER = 'footer';

/** La liste des catégories et sous-catégories */
export const Body = styled.div`
  grid-area: ${AREA_BODY};
`;

/**
 * L'élément de gauche, un lien contenant une image
 * Si pas de lien, on aria-disable et bloque le clic
 */
export const Aside = styled(Link)`
  display: block;
  overflow: hidden;
  margin-right: 40px;
  align-self: flex-start;
  grid-area: ${AREA_ASIDE};

  border-radius: 4px;
  background: ${COLORS.NEUTRAL_PLACEHOLDER};
  aspect-ratio: ${ImageParams.familyAside.w} / ${ImageParams.familyAside.h};

  img {
    width: 100%;
    display: block;
    object-fit: cover;
    border-radius: 4px;
  }

  &[aria-disabled='true'] {
    pointer-events: none;
  }
`;

/** Le bandeau de réassurance, sous les catégories */
export const Footer = styled.div`
  margin-top: 24px;
  grid-area: ${AREA_FOOTER};
`;

/**
 * La grille qui organise l'affichage du menu. Est composé de 3 parties :
 * - `Side` : Image d'accroche à gauche
 * - `Body` : Grille des catégories à droite
 * - `Foot` : Bandeau de réassurance tout en bas
 */
export const Layout = styled.div`
  display: grid;
  min-height: 100%;

  grid-template:
    '${AREA_ASIDE}  ${AREA_BODY}  ' 1fr
    '${AREA_FOOTER} ${AREA_FOOTER}' auto
    / auto 1fr;
`;

/**
 * Le conteneur principal qui défini la taille et la position du menu :
 * - Positionné en absolute juste en dessous de la carte de la famille
 * - Marge par défaut égale à celle du site pour s'aligner avec le contenu
 * - Hauteur maximale pour ne pas dépasser en bas sur les petites résolutions
 * - Visible uniquement si précédé d'un [aria-selected="true"] (carte famille)
 */
export const Container = styled.div<{ $count: number; $hasImage: boolean }>`
  display: none;
  position: absolute;
  inset: 100% 0 auto;
  z-index: ${ZINDEX.familiesMenu};

  padding: 24px;

  max-height: calc(100vh - 200px);

  overflow: auto;
  overscroll-behavior: contain;

  border-radius: 4px;
  background: ${COLORS.NEUTRAL_WHITE};
  box-shadow: ${SPECIALS.SHADOW_NORMAL};

  [aria-selected='true'] > & {
    display: block;
  }

  ${({ $count, $hasImage }) => {
    const config = getLayout({ count: $count, hasImage: $hasImage });

    // Liste des ajustement apportés à la grille des catégories
    // - Masque les sous-catégories à partir de la position N
    // - Si la catégorie ne contient pas au moins N+1 sous-catégories
    //   -> Masque le bouton "Voir plus"
    //   -> Affiche toutes les sous-catégories
    const layouts = config.layouts.map(
      ({ range, aside, grid }) => css`
        @media ${getMediaQuery(range.min, range.max)} {
          ${Aside} {
            width: ${aside.width}px;
            display: ${aside.show ? 'block' : 'none'};
          }

          .${categoryGridStyles.container} {
            grid-template-columns: repeat(${grid.cols}, 1fr);

            .${subCategoriesStyles.item}:nth-child(n + ${grid.subs}) {
              display: none;
            }

            .${subCategoriesStyles.list}:not(:has(.${subCategoriesStyles.item}:nth-child(${grid.subs +
              1}))) {
              + .${categoryItemStyles.more} {
                display: none;
              }
              > .${subCategoriesStyles.item} {
                display: flex;
              }
            }
          }
        }
      `,
    );

    return css`
      margin: 8px max(${styles.CATALOG_SIDE_PADDING_V2}px, 50vw - ${config.maxWidth / 2}px);
      ${layouts}
    `;
  }}
`;
